import type { ContentItem } from '../../api/content';

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info';

function isWebsiteRefreshItem(item: Pick<ContentItem, 'metadata'>): boolean {
  const meta = (item.metadata as Record<string, unknown> | undefined) ?? {};
  return !!meta.websiteRefresh;
}

export function getDisplayStatusLabel(item: Pick<ContentItem, 'status' | 'metadata'>): string {
  if (item.status === 'draft') return 'Draft';
  if (item.status === 'review') return 'In review';
  if (item.status === 'approved') return 'Approved';
  if (item.status === 'published') {
    return isWebsiteRefreshItem(item as Pick<ContentItem, 'metadata'>) ? 'Approved' : 'Published';
  }
  return item.status;
}

export function getDisplayStatusVariant(item: Pick<ContentItem, 'status'>): BadgeVariant {
  if (item.status === 'review') return 'warning';
  if (item.status === 'approved' || item.status === 'published') return 'success';
  return 'default';
}

/**
 * A post seeded into the published library from real, already-posted content
 * (via the "Import published posts" modal or the seed script), as opposed to
 * one the OS generated. Used to badge imported posts distinctly.
 */
export function isImportedPost(item: Pick<ContentItem, 'metadata'>): boolean {
  const source = (item.metadata as Record<string, unknown> | undefined)?.source;
  return source === 'manual_import' || source === 'manual_seed';
}

/**
 * The reviewer count worth a pill on a library card. "In review" with nobody
 * approved yet is the normal state (the status pill already says it), so it
 * gets none. A pill appears only when the item is part-way (1/2 approved) or
 * stuck: every reviewer approved but the item is still in review.
 */
export function approvalNote(
  item: Pick<ContentItem, 'status' | 'metadata'>,
): { label: string; variant: BadgeVariant } | null {
  if (item.status !== 'review') return null;
  const reviewers = ((item.metadata as Record<string, unknown> | undefined)?.review as
    | { reviewers?: Array<{ status: string }> }
    | undefined)?.reviewers;
  if (!reviewers || reviewers.length === 0) return null;
  const approved = reviewers.filter((r) => r.status === 'approved').length;
  if (approved === 0) return null;
  if (approved === reviewers.length) return { label: `All ${approved} approved, still in review`, variant: 'warning' };
  return { label: `${approved}/${reviewers.length} approved`, variant: 'default' };
}

/** An approved LinkedIn post should be posted within this many days of approval. */
export const STALE_APPROVAL_DAYS = 14;

/**
 * Days since an approved LinkedIn post was approved, when that is more than
 * `STALE_APPROVAL_DAYS` and it still isn't marked as posted; otherwise null.
 * Approval date is the latest reviewer approval, else the last update. On 12 Oct
 * all 9 approved posts were 89 to 110 days old.
 */
export function staleApprovalDays(
  item: Pick<ContentItem, 'type' | 'status' | 'metadata' | 'updatedAt'>,
  now: Date = new Date(),
): number | null {
  if (item.type !== 'linkedin_post' || item.status !== 'approved') return null;
  const reviewers = ((item.metadata as Record<string, unknown> | undefined)?.review as
    | { reviewers?: Array<{ approvedAt?: string | null }> }
    | undefined)?.reviewers;
  const approvals = (reviewers ?? []).map((r) => r.approvedAt).filter((d): d is string => !!d).sort();
  const approvedAt = approvals[approvals.length - 1] ?? item.updatedAt;
  const days = Math.floor((now.getTime() - Date.parse(approvedAt)) / 86_400_000);
  return Number.isFinite(days) && days > STALE_APPROVAL_DAYS ? days : null;
}
